import { crashErrorClass, KNOWN_ERROR_CLASSES } from './page-crash-detail';

/**
 * Diagnosis for a crash on a PUBLIC marketing page (#7302). The minimal
 * beacon (#7309) names only the error class, digest, route pattern and asset
 * file. A real `/zh` SyntaxError in production could not be diagnosed from
 * that, so for crashes ONLY on the static list of public marketing routes
 * below (no sign-in, no workspace, no user content on the page) and ONLY for
 * SyntaxError and chunk-load failures, the beacon also carries a REASON
 * ENUM (a fixed vocabulary; no part of the message), the failing first-party script as `host/path` and its
 * `line:col`. Every other route and class stays on the minimal fields.
 *
 * The route list is STATIC: a new page does not opt in by accident. The
 * tests enumerate `src/lib/marketing-route-manifest` and `src/app` against it.
 */

/** Locale prefixes of the public marketing pages (every locale but `en`). */
export const MARKETING_LOCALE_PREFIXES: readonly string[] = [
  'ar',
  'de',
  'es',
  'pt-BR',
  'fr',
  'hi',
  'id',
  'it',
  'ja',
  'ko',
  'ru',
  'vi',
  'zh',
  'zh-TW',
];
/** The public marketing pages (`src/lib/marketing-route-manifest`). */
export const PUBLIC_MARKETING_PATHS: readonly string[] = [
  '/',
  '/pricing',
  '/contact',
  '/privacy',
  '/terms',
  '/apparel',
  '/footwear',
  '/bags-accessories',
  '/eyewear',
  '/jewelry-watches',
  '/packaging',
  '/furniture',
  '/beauty-personal-care',
  '/toys',
  '/sporting-goods',
];
/** `/roles/<slug>` pages (`MARKETING_ROLES`). */
export const PUBLIC_ROLE_SLUGS: readonly string[] = [
  'founders-emerging-brands',
  'creative-directors',
  'fashion-designers',
  'technical-design-development',
  'marketing-ecommerce',
  'merchandisers-buyers',
  'sourcing-supply-chain',
  'manufactures-factories',
];

/** Whether `pathname` is exactly one of the public marketing pages, in any locale. */
export function isPublicMarketingRoute(
  pathname: string | null | undefined
): boolean {
  if (
    typeof pathname !== 'string' ||
    pathname.length > 200 ||
    !pathname.startsWith('/')
  )
    return false;
  const segments = pathname
    .replace(/\/+$/, '')
    .split('/')
    .filter((part) => part.length > 0);
  if (
    segments.length > 0 &&
    MARKETING_LOCALE_PREFIXES.includes(segments[0] ?? '')
  )
    segments.shift();
  const path = `/${segments.join('/')}`;
  if (PUBLIC_MARKETING_PATHS.includes(path)) return true;
  return (
    segments.length === 2 &&
    segments[0] === 'roles' &&
    PUBLIC_ROLE_SLUGS.includes(segments[1] ?? '')
  );
}

const MESSAGE_INPUT_CAP = 600;
const STACK_INPUT_CAP = 6000;
const STACK_LINES_MAX = 40;

/**
 * What the crash was, as a FINITE vocabulary: the error message is mapped onto
 * these known engine/framework templates and ONLY the enum leaves the browser.
 * No substring of the original message is ever sent, so there is nothing to
 * sanitize (client-side scrubbing of free text loses to encodings, Unicode
 * separators, credentials and tokens). The offending token is named only
 * when it is one char of a tiny allowlist, and only as an enum member.
 */
export const CRASH_REASONS: ReadonlySet<string> = new Set([
  'unexpected_token_lt',
  'unexpected_token_rbrace',
  'unexpected_token_rparen',
  'unexpected_token_rbracket',
  'unexpected_token_comma',
  'unexpected_token_semicolon',
  'unexpected_token',
  'unexpected_end_of_input',
  'invalid_or_unexpected_token',
  'unexpected_identifier',
  'unexpected_string',
  'unexpected_number',
  'missing_paren_after_arguments',
  'json_parse',
  'chunk_load_failed',
  'css_chunk_failed',
  'dynamic_import_failed',
  'module_script_failed',
  'unknown',
]);
const CHUNK_REASONS: ReadonlySet<string> = new Set([
  'chunk_load_failed',
  'css_chunk_failed',
  'dynamic_import_failed',
  'module_script_failed',
]);
const TOKEN_REASONS: Readonly<Record<string, string>> = {
  '<': 'unexpected_token_lt',
  '}': 'unexpected_token_rbrace',
  ')': 'unexpected_token_rparen',
  ']': 'unexpected_token_rbracket',
  ',': 'unexpected_token_comma',
  ';': 'unexpected_token_semicolon',
};

/** `(line N column M)` suffix V8 adds to JSON positions. */
const POSITION = String.raw`(?: \(line \d{1,9} column \d{1,9}\))?`;
const IN_JSON = String.raw`in JSON at position \d{1,9}${POSITION}`;
const CHUNK_DETAIL = String.raw`\((?:error|missing|timeout): [^\n]{0,500}\)`;

/**
 * Known engine/framework templates, each anchored at the START and the END
 * (`^...$`) so a message that merely mentions a template, or has a payload
 * after it, never matches. Evaluated in order; the first match wins. Only the
 * enum on the right ever leaves the browser.
 */
const REASON_TEMPLATES: ReadonlyArray<readonly [RegExp, string]> = [
  [
    new RegExp(`^Loading CSS chunk [\\w-]{1,40} failed\\.\\n${CHUNK_DETAIL}$`),
    'css_chunk_failed',
  ],
  [
    /^Failed to load CSS chunk \S{1,400}(?: from module [\w-]{1,40})?$/,
    'css_chunk_failed',
  ],
  [
    new RegExp(`^Loading chunk [\\w-]{1,40} failed\\.\\n${CHUNK_DETAIL}$`),
    'chunk_load_failed',
  ],
  [
    /^Failed to load chunk \S{1,400}(?: from module [\w-]{1,40})?$/,
    'chunk_load_failed',
  ],
  [
    /^(?:Failed to fetch dynamically imported module|error loading dynamically imported module): \S{1,400}$/,
    'dynamic_import_failed',
  ],
  [/^Importing a module script failed\.?$/, 'module_script_failed'],
  // An HTML page where script was expected: any message that STARTS with
  // `Unexpected token '<'` is this diagnosis, whatever follows (a JSON.parse of
  // an HTML page is the same cause).
  [/^Unexpected token '<'(?:[\s\S]*)$/, 'unexpected_token_lt'],
  // Real V8/Chromium JSON.parse templates (the quoted input is never read).
  [
    new RegExp(
      `^Unexpected token '[^'\\n]{1,8}', "[\\s\\S]{0,80}"(?:\\.\\.\\.)? is not valid JSON$`
    ),
    'json_parse',
  ],
  [/^"[\s\S]{0,80}"(?:\.\.\.)? is not valid JSON$/, 'json_parse'],
  [/^Unexpected end of JSON input$/, 'json_parse'],
  [
    new RegExp(
      `^Unexpected non-whitespace character after JSON at position \\d{1,9}${POSITION}$`
    ),
    'json_parse',
  ],
  [
    new RegExp(
      `^Unexpected (?:number|string|identifier "[^"\\n]{1,40}"|token .{1,3}) ${IN_JSON}$`
    ),
    'json_parse',
  ],
  [new RegExp(`^Expected [^\\n]{1,60} ${IN_JSON}$`), 'json_parse'],
  [
    new RegExp(
      `^(?:Unterminated string|Bad control character in string literal|Bad escaped character|Bad Unicode escape|Exponent part is missing a number|No number after minus sign|Unterminated fractional number) ${IN_JSON}$`
    ),
    'json_parse',
  ],
  [
    /^JSON\.parse: [a-z' ]{1,60} at line \d{1,9} column \d{1,9} of the JSON data$/,
    'json_parse',
  ],
  [/^JSON Parse error: [A-Za-z' ]{1,60}$/, 'json_parse'],
  // Script compile errors.
  [/^Unexpected end of input$/, 'unexpected_end_of_input'],
  [/^Invalid or unexpected token$/, 'invalid_or_unexpected_token'],
  [/^Unexpected identifier(?: '[^'\n]{1,60}')?$/, 'unexpected_identifier'],
  [/^Unexpected string$/, 'unexpected_string'],
  [/^Unexpected number$/, 'unexpected_number'],
  [/^missing \) after argument list$/, 'missing_paren_after_arguments'],
];

/** The enum for an error message: a complete known template, else `unknown`. */
export function crashReason(error: unknown): string {
  const raw = (error as { message?: unknown } | null)?.message;
  const message =
    typeof raw === 'string' ? raw.slice(0, MESSAGE_INPUT_CAP) : '';
  for (const [pattern, reason] of REASON_TEMPLATES) {
    if (pattern.test(message)) return reason;
  }
  // A single allowlisted offending char, or any other short token, is a
  // generic unexpected token. The whole message must be that template.
  const token = /^Unexpected token '([^'\n]{1,40})'$/.exec(message);
  if (token) return TOKEN_REASONS[token[1] ?? ''] ?? 'unexpected_token';
  return 'unknown';
}

/** A SyntaxError, or a script/chunk load failure (by class or by its template). */
export function isDiagnosableCrash(error: unknown): boolean {
  const errorClass = crashErrorClass(error);
  return (
    errorClass === 'SyntaxError' ||
    errorClass === 'ChunkLoadError' ||
    CHUNK_REASONS.has(crashReason(error))
  );
}

const SCRIPT_PATH =
  /^\/_next\/static\/(?:[A-Za-z0-9._%~@+-]{1,120}\/){0,6}[A-Za-z0-9._%~@+-]{1,120}\.(?:m?js|css)$/;
const FRAME =
  /(?:https?:)?\/\/([A-Za-z0-9.-]{1,120}(?::\d{1,5})?)(\/_next\/static\/[^\s():?#]{1,400})(?:[?#][^\s():]*)?:(\d{1,7}):(\d{1,7})/;
const MESSAGE_SCRIPT =
  /(?:https?:)?\/\/([A-Za-z0-9.-]{1,120}(?::\d{1,5})?)(\/_next\/static\/[^\s()'"?#]{1,400}\.(?:m?js|css))/;

export interface CrashScriptLocation {
  /** `host/path`, first-party, no query or hash. */
  url: string;
  line?: number;
  col?: number;
}

/**
 * The failing first-party script: the first stack frame inside `/_next/static/`
 * (`url:line:col`), else (chunk-load) the script URL the message names.
 */
export function crashScriptLocation(
  error: unknown,
  firstPartyHost?: string | null
): CrashScriptLocation | undefined {
  if (!firstPartyHost) return undefined;
  const host = firstPartyHost.toLowerCase();
  const stack = (error as { stack?: unknown } | null)?.stack;
  if (typeof stack === 'string') {
    for (const line of stack
      .slice(0, STACK_INPUT_CAP)
      .split('\n')
      .slice(0, STACK_LINES_MAX)) {
      const match = FRAME.exec(line);
      if (
        match &&
        match[1]?.toLowerCase() === host &&
        SCRIPT_PATH.test(match[2] ?? '')
      ) {
        return {
          url: `${match[1]}${match[2]}`,
          line: Number(match[3]),
          col: Number(match[4]),
        };
      }
    }
  }
  const message = (error as { message?: unknown } | null)?.message;
  const named =
    typeof message === 'string'
      ? MESSAGE_SCRIPT.exec(message.slice(0, MESSAGE_INPUT_CAP))
      : null;
  if (
    named &&
    named[1]?.toLowerCase() === host &&
    SCRIPT_PATH.test(named[2] ?? '')
  ) {
    return { url: `${named[1]}${named[2]}` };
  }
  return undefined;
}

export interface CrashDiagnosis {
  /** An enum member of `CRASH_REASONS`: never any part of the message. */
  error_reason?: string;
  script_url?: string;
  error_line?: number;
  error_col?: number;
}

/** The diagnosis fields for a SyntaxError / chunk-load crash, else nothing. */
export function buildCrashDiagnosis(
  error: unknown,
  firstPartyHost?: string | null
): CrashDiagnosis {
  if (!isDiagnosableCrash(error)) return {};
  const script = crashScriptLocation(error, firstPartyHost);
  return {
    error_reason: crashReason(error),
    ...(script ? { script_url: script.url } : {}),
    ...(script?.line ? { error_line: script.line } : {}),
    ...(script?.col ? { error_col: script.col } : {}),
  };
}

/** The keys a crash body's properties may have, each with its finite value rule. */
const BASE_RULES: Record<string, (value: unknown) => boolean> = {
  surface: (v) => v === 'page_crash',
  operation: (v) =>
    typeof v === 'string' &&
    /^(?:unknown|chunk_load_fallback\/(?:repeat|unsafe|storage))$/.test(v),
  error_kind: (v) => v === 'client_error',
  error_code: (v) =>
    typeof v === 'string' &&
    (KNOWN_ERROR_CLASSES.has(v) || v === 'UnknownError'),
  error_name: (v) =>
    typeof v === 'string' &&
    (KNOWN_ERROR_CLASSES.has(v) || v === 'UnknownError'),
  error_digest: (v) => typeof v === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(v),
  route_pattern: (v) =>
    typeof v === 'string' &&
    v.length <= 200 &&
    /^(?:\/(?:[A-Za-z0-9_.-]+|\[[A-Za-z]+\]))*\/?$/.test(v),
  alert_key: (v) =>
    typeof v === 'string' && /^[A-Za-z0-9_.:/[\]-]{1,300}$/.test(v),
  probe: (v) => v === true,
  // Host + file for an app route; host + path only with diagnosis (below).
  script_url: (v) =>
    typeof v === 'string' &&
    /^[A-Za-z0-9.-]{1,120}(?::\d{1,5})?\/[A-Za-z0-9._%~@+-]{1,120}\.(?:m?js|css)$/.test(
      v
    ),
};
const DIAGNOSIS_RULES: Record<string, (value: unknown) => boolean> = {
  script_url: (v) =>
    typeof v === 'string' &&
    v.length <= 600 &&
    /^[A-Za-z0-9.-]{1,120}(?::\d{1,5})?(?:\/[A-Za-z0-9._%~@+-]{1,120}){1,8}\.(?:m?js|css)$/.test(
      v
    ),
  error_reason: (v) => typeof v === 'string' && CRASH_REASONS.has(v),
  error_line: (v) =>
    Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 9_999_999,
  error_col: (v) =>
    Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 9_999_999,
};

/**
 * A FRESH properties object holding only allowlisted keys whose value passes
 * its rule. The diagnosis keys survive only with `allowDiagnosis` (a public
 * marketing route); on any other route they are dropped, and `script_url`
 * falls back to the minimal host/file rule.
 */
export function finalizePageCrashProperties(
  properties: Record<string, unknown>,
  { allowDiagnosis }: { allowDiagnosis: boolean }
): Record<string, unknown> {
  const rules = allowDiagnosis
    ? { ...BASE_RULES, ...DIAGNOSIS_RULES }
    : BASE_RULES;
  const out: Record<string, unknown> = {};
  for (const [key, rule] of Object.entries(rules)) {
    const value = properties[key];
    if (value !== undefined && rule(value)) out[key] = value;
  }
  return out;
}
